/**
 * BridgeShell — full-viewport grid frame for the Fleet HUD grand bridge
 * (Wave 2, Item 1). Rendered INSTEAD of AppLayout when bridge mode is on at
 * lg+. Pure re-slotting: every fleet component slotted here is untouched.
 *
 * CONSUMERS:
 * - frontend/src/pages/FleetHud.tsx (bridge render path only)
 *
 * Layout contract (docs/plans/2026-08-12-hud-bridge-wave2-plan.md §1):
 *   h-screen flex-col → 48px top bar → three-region clamp grid → bottom strip.
 *   grid-cols-[clamp(260px,21vw,320px)_minmax(0,1fr)_clamp(260px,21vw,320px)]
 *
 * Chrome rules: rounded-sm or rounded-none ONLY (never rounded-lg), hairline
 * border-white/[0.06] panels, near-black base + #FF6600 accents + DM Mono. // design-law-allow
 *
 * Atmosphere: the background grid + vignette are 100% STATIC CSS — zero
 * keyframes, zero animation — so prefers-reduced-motion needs no special
 * path for them. Item 10 adds <EmberField> as a decorative sibling in the
 * same layer; it owns its own reduced-motion null guard and lg-only gate,
 * leaving the board fully functional when absent. The grid is
 * 1px lines, white at 2.5%, 48px cells, radial mask fading to 70% opacity
 * from center, plus a base vignette overlay (plan §3).
 */
import React, { ReactNode } from 'react';
import BridgeTopBar from './BridgeTopBar';
import EmberField from './EmberField';
import type { FleetMood } from '../fleet/fleetMood';

interface BridgeShellProps {
  leftRail: ReactNode;
  center: ReactNode;
  rightRail: ReactNode;
  bottomStrip: ReactNode;
  /** Top-bar state — mounted as <BridgeTopBar> (item 2); the shell owns no
      chrome controls of its own (one relocation, zero duplication). */
  mood: FleetMood;
  polledAt: number | null;
  pollMs: number;
  isRunActive: boolean;
  onToggleCompact: () => void;
  onExitToConsole: () => void;
  /**
   * COMPACT sub-mode: same bridge chrome (top bar + bottom strip stay put so
   * the toggle and ⌂ CONSOLE exit remain reachable), but the three-column
   * grid collapses to a single stacked scroll column (plan §5's stacked
   * variant, item-1 minimal form).
   */
  compact?: boolean;
}

// Static atmosphere — no keyframes anywhere in this file (reduced-motion
// safe by construction, not by media query).
const GRID_STYLE: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(to right, rgba(255,255,255,0.025) 1px, transparent 1px),' +
    'linear-gradient(to bottom, rgba(255,255,255,0.025) 1px, transparent 1px)',
  backgroundSize: '48px 48px',
  WebkitMaskImage:
    'radial-gradient(ellipse at center, rgba(0,0,0,1) 0%, rgba(0,0,0,0.7) 100%)',
  maskImage:
    'radial-gradient(ellipse at center, rgba(0,0,0,1) 0%, rgba(0,0,0,0.7) 100%)',
};

const VIGNETTE_STYLE: React.CSSProperties = {
  background:
    'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.55) 100%)',
};

// Item 9 atmosphere: the MOOD GRADE — a second, colored vignette whose hue is
// the page-level --mood-glow custom property (set by FleetHud from
// MOOD_STYLE[mood]). A halt shifts the whole room red-orange: the 3-meter
// test. Static CSS; the only thing that changes is the variable's value on a
// real mood transition, so no animation exists to collapse under
// reduced-motion.
const MOOD_GRADE_STYLE: React.CSSProperties = {
  background:
    'radial-gradient(ellipse at center, transparent 60%, var(--mood-glow, transparent) 140%)',
  opacity: 0.22,
};

export default function BridgeShell({
  leftRail,
  center,
  rightRail,
  bottomStrip,
  mood,
  polledAt,
  pollMs,
  isRunActive,
  onToggleCompact,
  onExitToConsole,
  compact = false,
}: BridgeShellProps) {
  return (
    <div
      data-testid="bridge-shell"
      // force-dark: the bridge is a dark room BY DESIGN — it re-pins the dark
      // token set for this subtree so the app-level light theme cannot leak in
      // (index.css `:root, .force-dark` block). colorScheme keeps native
      // scrollbars/inputs dark to match.
      className="force-dark relative flex h-screen flex-col overflow-hidden bg-base font-mono text-text-primary"
      style={{ colorScheme: 'dark' }}
    >
      {/* Atmosphere layer — static grid + vignette, behind all chrome. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={GRID_STYLE} />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={VIGNETTE_STYLE} />
      <div
        aria-hidden="true"
        data-testid="bridge-mood-grade"
        className="pointer-events-none absolute inset-0"
        style={MOOD_GRADE_STYLE}
      />
      {/* Item 10 garnish — mood-driven ember field. Purely decorative: renders
          null under prefers-reduced-motion and is hidden below lg, so the
          board is fully functional without it. Additive sibling only. */}
      <EmberField mood={mood} />

      {/* TOP BAR · 48px frame — item 2's BridgeTopBar (44px) owns ALL chrome:
          identity, mood pill, poll countdown, BRIDGE/COMPACT, ⌂ CONSOLE. */}
      <header
        data-testid="bridge-topbar"
        className="relative z-10 flex h-12 shrink-0 items-center border-b border-white/[0.06] bg-base"
      >
        <BridgeTopBar
          mood={mood}
          polledAt={polledAt}
          pollMs={pollMs}
          isRunActive={isRunActive}
          compact={compact}
          onToggleCompact={onToggleCompact}
          onExitToConsole={onExitToConsole}
        />
      </header>

      {compact ? (
        /* COMPACT sub-mode — bridge chrome, single stacked scroll column. */
        <main
          data-testid="bridge-compact-stack"
          className="relative z-10 min-h-0 flex-1 overflow-y-auto"
        >
          <div className="mx-auto flex max-w-3xl flex-col gap-3 px-4 py-4">
            {leftRail}
            {center}
            {rightRail}
          </div>
        </main>
      ) : (
        /* THREE-REGION GRID — plan §1 proportions. AlivenessStrip is NOT in
            here: it is pinned below the grid in the footer. */
        <main
          data-testid="bridge-grid"
          className="relative z-10 grid min-h-0 flex-1 grid-cols-[clamp(260px,21vw,320px)_minmax(0,1fr)_clamp(260px,21vw,320px)]"
        >
          <aside
            data-testid="bridge-left-rail"
            className="overflow-hidden border-r border-white/[0.06] bg-[rgba(10,10,12,0.72)] backdrop-blur-[10px]"
          >
            {/* overflow-hidden (not auto): the rail's blocks own their scroll
                — the agent roster is the flex-grow scrolling region (plan §1
                "AGENTS … scrolls, flex-grow"), STATUS/ACTION REQUIRED stay
                pinned above it. */}
            <div className="flex h-full flex-col gap-3 overflow-hidden p-3">{leftRail}</div>
          </aside>
          <section data-testid="bridge-center" className="overflow-hidden">
            <div className="flex h-full flex-col gap-3 overflow-y-auto p-3">{center}</div>
          </section>
          <aside
            data-testid="bridge-right-rail"
            className="overflow-hidden border-l border-white/[0.06] bg-[rgba(10,10,12,0.72)] backdrop-blur-[10px]"
          >
            <div className="flex h-full flex-col gap-3 overflow-y-auto p-3">{rightRail}</div>
          </aside>
        </main>
      )}

      {/* BOTTOM STRIP — AlivenessStrip pinned below the grid, full width. */}
      <footer
        data-testid="bridge-bottom"
        className="relative z-10 shrink-0 border-t border-white/[0.06] bg-base"
      >
        {bottomStrip}
      </footer>
    </div>
  );
}
